import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  answerQuestion,
  postQuestion,
  uploadQuestionImage,
  watchLessonQuestions,
  type LessonQuestion,
} from "../lib/firebase";
import { questionAuthorLabel, sortQuestions, type QuestionView } from "../lib/qa";

type Props = {
  courseId: string;
  lessonId: string;
  user: { uid: string; email: string | null; displayName: string | null };
  isAdmin: boolean;
};

function toView(q: LessonQuestion): QuestionView {
  return { ...q };
}

export function LessonQuestions({ courseId, lessonId, user, isAdmin }: Props) {
  const [questions, setQuestions] = useState<LessonQuestion[]>([]);
  const [text, setText] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const unsub = watchLessonQuestions(courseId, lessonId, setQuestions);
    return unsub;
  }, [courseId, lessonId]);

  // Arriving via a notification link (#q-{id}): scroll to that question and
  // flash it. Retries on every questions update until the element exists.
  const { hash } = useLocation();
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    if (!hash.startsWith("#q-") || scrolledTo.current === hash) return;
    const el = document.getElementById(hash.slice(1));
    if (!el) return;
    scrolledTo.current = hash;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("qa-item--flash");
  }, [hash, questions]);

  // Build/tear down a local preview URL whenever the attached image changes.
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function attachImage(next: File | null) {
    setFile(next);
    setError(null);
  }

  function clearImage() {
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
  }

  // Ctrl+V / Cmd+V a screenshot straight into the form - no need to save-then-attach.
  function handlePaste(e: React.ClipboardEvent) {
    const img = Array.from(e.clipboardData.items).find((it) => it.type.startsWith("image/"));
    if (!img) return; // let normal text paste through
    const blob = img.getAsFile();
    if (!blob) return;
    e.preventDefault();
    // Clipboard images often come as a generic "image.png" (or no name) - give it a stable one.
    const ext = blob.type.split("/")[1] || "png";
    attachImage(new File([blob], `pasted-${Date.now()}.${ext}`, { type: blob.type }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      let imageUrl: string | undefined;
      if (file) imageUrl = await uploadQuestionImage(courseId, lessonId, user.uid, file);
      await postQuestion(courseId, lessonId, user, { text: text.trim(), imageUrl, isAnonymous: anonymous });
      setText("");
      setAnonymous(false);
      clearImage();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const sorted = sortQuestions(questions.map(toView));

  return (
    <section className="lesson-qa">
      <div className="lesson-qa__header">
        <h2 className="lesson-qa__title">💬 มีคำถามไหม? ถามได้เลย</h2>
        <p className="lesson-qa__subtitle">สงสัยตรงไหนถามได้ทันที ผู้สอนมาตอบให้เอง ถามแบบไม่ระบุชื่อก็ได้</p>
      </div>

      <form className="lesson-qa__form" onSubmit={handleSubmit}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={handlePaste}
          placeholder="สงสัยตรงไหน พิมพ์ถามได้เลย แนบรูปหน้าจอ หรือกด Ctrl+V วางรูปที่แคปไว้ได้เลย"
          rows={3}
        />
        {preview && (
          <div className="lesson-qa__preview">
            <img src={preview} alt="รูปที่จะแนบ" />
            <button type="button" className="lesson-qa__preview-remove" onClick={clearImage} aria-label="ลบรูป">
              ✕ ลบรูป
            </button>
          </div>
        )}
        <div className="lesson-qa__form-row">
          <label className="lesson-qa__attach">
            📎 แนบรูป
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => attachImage(e.target.files?.[0] ?? null)}
            />
          </label>
          <span className="lesson-qa__paste-hint">หรือกด Ctrl+V วางรูปที่แคปไว้</span>

          <label className="lesson-qa__anon">
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
            ถามแบบไม่ระบุชื่อ
          </label>

          <button type="submit" className="btn-primary" disabled={submitting || !text.trim()}>
            {submitting ? "กำลังส่ง…" : "ส่งคำถาม"}
          </button>
        </div>
        {anonymous && (
          <small className="lesson-qa__anon-note">
            เพื่อนนักเรียนจะไม่เห็นชื่อคุณ (ผู้สอนยังเห็นเพื่อความปลอดภัย) ถามได้สบายใจ ไม่ต้องเขิน
          </small>
        )}
        {error && <p className="error">{error}</p>}
      </form>

      <div className="lesson-qa__list">
        {sorted.length === 0 && (
          <p style={{ color: "var(--c-text-muted)" }}>ยังไม่มีคำถาม เป็นคนแรกที่ถามได้เลย</p>
        )}
        {sorted.map((q) => (
          <article key={q.id} id={`q-${q.id}`} className="qa-item">
            <header className="qa-item__head">
              <strong>{questionAuthorLabel(q)}</strong>
              <span className="qa-item__date">{q.createdAt.toLocaleDateString("th-TH")}</span>
              {!q.answerText && <span className="qa-item__pending">รอตอบ</span>}
            </header>
            <p className="qa-item__text">{q.text}</p>
            {q.imageUrl && (
              <a href={q.imageUrl} target="_blank" rel="noreferrer">
                <img src={q.imageUrl} alt="แนบโดยผู้ถาม" className="qa-item__img" />
              </a>
            )}

            {q.answerText ? (
              <AnswerBlock courseId={courseId} lessonId={lessonId} q={q} isAdmin={isAdmin} />
            ) : (
              isAdmin && (
                <AdminAnswerBox courseId={courseId} lessonId={lessonId} questionId={q.id} questionText={q.text} />
              )
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

/** The published answer; admin gets an edit button that swaps in the answer box. */
function AnswerBlock({
  courseId,
  lessonId,
  q,
  isAdmin,
}: {
  courseId: string;
  lessonId: string;
  q: QuestionView;
  isAdmin: boolean;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <AdminAnswerBox
        courseId={courseId}
        lessonId={lessonId}
        questionId={q.id}
        questionText={q.text}
        initial={q.answerText ?? ""}
        onDone={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="qa-item__answer">
      <strong>Dewviko AI (AI Coach)</strong>
      <p>{q.answerText}</p>
      {isAdmin && (
        <button type="button" className="qa-item__edit" onClick={() => setEditing(true)}>
          ✏️ แก้ไขคำตอบ
        </button>
      )}
    </div>
  );
}

function AdminAnswerBox({
  courseId,
  lessonId,
  questionId,
  questionText,
  initial,
  onDone,
}: {
  courseId: string;
  lessonId: string;
  questionId: string;
  questionText: string;
  /** Existing answer when editing; undefined for a first answer. */
  initial?: string;
  onDone?: () => void;
}) {
  const isEdit = initial !== undefined;
  const [answer, setAnswer] = useState(initial ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!answer.trim() || saving) return;
    setSaving(true);
    setErr(null);
    try {
      // Notify the author only on a first answer - edits don't re-ping them.
      await answerQuestion(courseId, lessonId, questionId, answer.trim(), isEdit ? undefined : { questionText });
      setAnswer("");
      onDone?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="qa-item__answer-box">
      <textarea
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder="ตอบคำถามนี้ (นักเรียนทุกคนจะเห็น)"
        rows={2}
      />
      <div className="qa-item__answer-actions">
        <button type="button" className="btn-primary" onClick={save} disabled={saving || !answer.trim()}>
          {saving ? "กำลังบันทึก…" : isEdit ? "บันทึกการแก้ไข" : "ตอบ"}
        </button>
        {isEdit && (
          <button type="button" className="btn-secondary" onClick={onDone} disabled={saving}>
            ยกเลิก
          </button>
        )}
      </div>
      {err && <p className="error">{err}</p>}
    </div>
  );
}
